import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanFace } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { folderPurposeOf, type FolderPurpose } from '@/lib/folderPurpose';
import { CheckFolderPicker } from './CheckFolderPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useDiscoveryClient } from '../api/context';
import { useFolders } from '../hooks/folders';

export interface PresenterRun {
  id: string; folderIds: string[]; status: 'queued' | 'running' | 'done' | 'error';
  total: number; processed: number; face: number; faceless: number; unknown: number; skipped: number;
  error: string | null; createdAt: string; updatedAt: string;
}
export const PRESENTER_OPTIONS = [
  { value: 'face', label: 'Face' }, { value: 'faceless', label: 'Faceless' },
  { value: 'unchecked', label: 'Not checked' }, { value: 'unknown', label: 'Unclear' },
];
export function PresenterCheckButton({ folderId, initialFolderIds = [], initialPurpose = '' }: {
  folderId?: string; initialFolderIds?: string[]; initialPurpose?: FolderPurpose | '';
}): JSX.Element {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(folderId ? 'folders' : 'all');
  const [folderIds, setFolderIds] = useState<string[]>(folderId ? [folderId] : []);
  const folders = useFolders();
  const [purpose, setPurpose] = useState<FolderPurpose | ''>(initialPurpose);
  const latest = useQuery({
    queryKey: ['discovery', 'presenter-checks', 'latest'], queryFn: () => client.presenterChecks.latest(),
    refetchInterval: (q) => q.state.data?.run && ['queued', 'running'].includes(q.state.data.run.status) ? 3000 : 15000,
    retry: false,
  });
  const run = latest.data?.run;
  const active = !!run && (run.status === 'queued' || run.status === 'running');
  const selectedFolders = scope === 'all' ? [] : folderIds;
  const validScope = scope === 'all' || folderIds.length > 0;
  const preview = useQuery({
    queryKey: ['discovery', 'presenter-checks', 'preview', selectedFolders],
    queryFn: () => client.presenterChecks.preview(selectedFolders), enabled: open && validScope && !active,
    staleTime: 0, retry: false,
  });
  useEffect(() => {
    if (!run || run.processed === 0) return;
    void qc.invalidateQueries({ queryKey: ['discovery', 'registry'] });
    void qc.invalidateQueries({ queryKey: ['discovery', 'folders'] });
    void qc.invalidateQueries({ queryKey: ['discovery', 'email'] });
  }, [run?.id, run?.processed, qc]);
  const start = useMutation({
    mutationFn: () => client.presenterChecks.start(selectedFolders),
    onSuccess: (result) => {
      qc.setQueryData(['discovery', 'presenter-checks', 'latest'], { enabled: true, run: result });
      void qc.invalidateQueries({ queryKey: ['discovery', 'presenter-checks', 'preview'] });
      setOpen(false);
      toast.success(`Checking ${result.total.toLocaleString()} unchecked creators in the background`);
    },
    onError: (err: Error) => { toast.error(err.message); void latest.refetch(); },
  });
  const shownRun = run && (!folderId || run.folderIds.length === 0 || run.folderIds.includes(folderId));
  return <div className="space-y-1">
    <Button size="sm" variant="outline" onClick={() => {
      if (!active) {
        const ids = folderId ? [folderId] : initialFolderIds.length ? initialFolderIds : initialPurpose
          ? (folders.data ?? []).filter((f) => folderPurposeOf(f.purpose) === initialPurpose).map((f) => f.publicId) : [];
        setPurpose(initialPurpose);
        setFolderIds(ids);
        setScope(folderId || initialPurpose || ids.length ? 'folders' : 'all');
      }
      setOpen(true);
    }}>
      <ScanFace className="h-3.5 w-3.5" />
      {active ? 'View face / faceless check' : 'Check face / faceless'}
    </Button>
    {shownRun && <p role="status" className="max-w-sm text-xs text-muted-foreground">
      {active ? `${run.processed.toLocaleString()} / ${run.total.toLocaleString()} checked · ${run.status}` :
        `${run.status === 'error' ? 'Stopped' : 'Last check'}: ${run.face} face · ${run.faceless} faceless · ${run.unknown} unclassified · ${run.skipped} skipped`}
    </p>}
    <Dialog open={open} onOpenChange={(value) => { if (!start.isPending) setOpen(value); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Check face / faceless</DialogTitle>
          <DialogDescription>Classify unchecked creators from their recent post thumbnails. Each creator is checked once across selected folders. Already checked and suppressed creators are skipped. Leads stay in their folders.</DialogDescription>
        </DialogHeader>
        {active && run ? <div className="space-y-3" role="status">
          <p>{run.processed.toLocaleString()} of {run.total.toLocaleString()} processed</p>
          <progress className="w-full" max={run.total} value={run.processed} aria-label="Face / faceless check progress" />
          <p className="text-sm text-muted-foreground">{run.face} face · {run.faceless} faceless · {run.unknown} unclassified · {run.skipped} skipped</p>
          <p className="text-sm text-muted-foreground">You can close this dialog and keep working. The check continues in the background.</p>
        </div> : <div className="space-y-4">
          <label className="block space-y-1 text-sm">Scope
            <select aria-label="Check scope" className="block w-full rounded-md border bg-background px-3 py-2" value={scope} disabled={start.isPending} onChange={(e) => setScope(e.target.value)}>
              <option value="all">All creators</option><option value="folders">Selected folders</option>
            </select>
          </label>
          {scope === 'folders' && <CheckFolderPicker folders={folders.data ?? []} value={folderIds} onChange={setFolderIds}
            purpose={purpose} onPurposeChange={setPurpose} disabled={start.isPending} />}
          {scope === 'folders' && folders.error && <p role="alert" className="text-sm text-destructive">Couldn't load folders. Please reopen the dialog to try again.</p>}
          <p className="text-sm" role="status">{!validScope ? 'Choose at least one folder.' : preview.isFetching ? 'Counting unchecked creators…' : preview.error ? `Couldn't count creators: ${(preview.error as Error).message}` : `${(preview.data?.total ?? 0).toLocaleString()} unchecked creators`}</p>
          {preview.data?.enabled === false && <p role="alert" className="text-sm text-destructive">Face / faceless checking is not configured on this server.</p>}
          {latest.error && <p role="alert" className="text-sm text-destructive">Couldn't load background checks: {(latest.error as Error).message}</p>}
          {run?.status === 'error' && <p role="alert" className="text-sm text-destructive">Last check stopped: {run.error}. Completed classifications are saved; a new check skips them.</p>}
          <p className="text-xs text-muted-foreground">Uses post lookups and an image analysis call per creator. Private accounts or missing thumbnails may remain unclassified. Other filters on the page do not limit this check; scope is set above.</p>
        </div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={start.isPending}>Close</Button>
          {!active && <Button disabled={start.isPending || !validScope || preview.isFetching || !preview.data?.total || !preview.data.enabled || !!latest.error} onClick={() => start.mutate()}>
            {start.isPending ? 'Starting…' : `Check ${(preview.data?.total ?? 0).toLocaleString()} unchecked creators`}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}
